import type { FC } from 'react';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import type { Locale, Month as MonthIndex } from 'date-fns';
import { getMonth, getYear, setMonth, setYear } from 'date-fns';

import { AVAILABLE_MAX_DATE, AVAILABLE_MIN_DATE } from './utils';

type MonthHeaderProps = {
  minDate: Date;
  maxDate: Date;
  currentDate: Date;
  setDate: (date: Date) => void;
  nextDisabled: boolean;
  prevDisabled: boolean;
  onClickNext: () => void;
  onClickPrevious: () => void;
  locale?: Locale;
};

const generateYears = ({ start, end }: { start: number; end: number }) =>
  Array.from({ length: end - start + 1 }, (_y, i) => start + i);

export const MonthHeader: FC<MonthHeaderProps> = ({
  minDate,
  maxDate,
  currentDate,
  setDate,
  nextDisabled,
  prevDisabled,
  onClickNext,
  onClickPrevious,
  locale,
}) => {
  const theme = useTheme();

  const availableYearRange = {
    start: (minDate ?? AVAILABLE_MIN_DATE).getFullYear(),
    end: (maxDate ?? AVAILABLE_MAX_DATE).getFullYear(),
  };

  const MONTHS = Array.from({ length: 12 }, (_, index) =>
    locale
      ? locale.localize?.month(index as MonthIndex, {
          width: 'abbreviated',
          context: 'standalone',
        })
      : [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ][index],
  );

  const handleMonthChange = (event: SelectChangeEvent<number>) => {
    setDate(setMonth(currentDate, parseInt(event.target.value as string, 10)));
  };

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    setDate(setYear(currentDate, parseInt(event.target.value as string, 10)));
  };

  const currentMonth = getMonth(currentDate);
  const currentYear = getYear(currentDate);
  const minYear = getYear(minDate);
  const maxYear = getYear(maxDate);
  const minMonthID = getMonth(minDate);
  const maxMonthID = getMonth(maxDate);

  const isDisabled = (month: number) => {
    if (currentYear === minYear || currentYear === maxYear) {
      if (currentYear === minYear && month < minMonthID) return true;
      if (currentYear === maxYear && month > maxMonthID) return true;
    }
    return false;
  };

  const navButtonSx = {
    'borderRadius': '8px',
    'color': theme.palette.grey[600],
    '.MuiSvgIcon-root': { color: theme.palette.grey[600] },
    '&:hover': { backgroundColor: theme.palette.grey[100] },
    '&.Mui-disabled': { backgroundColor: 'transparent' },
  };

  return (
    <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }}>
      <Box sx={{ ml: '10px' }}>
        <IconButton
          disableRipple
          size="small"
          disabled={prevDisabled}
          onClick={onClickPrevious}
          sx={navButtonSx}
        >
          <KeyboardArrowLeft
            fontSize="small"
            sx={{
              fill: prevDisabled ? theme.palette.grey[400] : undefined,
            }}
          />
        </IconButton>
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <Box>
        <FormControl>
          <Select
            variant="outlined"
            size="small"
            defaultValue={currentYear}
            IconComponent={(iconProps) => (
              <KeyboardArrowDown
                fontSize="small"
                sx={{ fill: theme.palette.grey[400] }}
                {...iconProps}
              />
            )}
            slotProps={{
              root: {
                sx: {
                  width: { sm: '100px', md: '120px' },
                  height: '30px',
                  backgroundColor: '#fff',
                },
              },
            }}
            MenuProps={{
              disablePortal: true,
              PaperProps: {
                sx: {
                  width: 'auto',
                  maxHeight: '224px',
                  boxShadow:
                    'rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px',
                },
              },
            }}
            value={currentMonth}
            onChange={handleMonthChange}
          >
            {MONTHS.map((month, idx) => (
              <MenuItem key={month} value={idx} disabled={isDisabled(idx)}>
                <Typography noWrap sx={{ fontSize: '14px' }}>
                  {month}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <FormControl>
          <Select
            variant="outlined"
            size="small"
            defaultValue={currentYear}
            IconComponent={(iconProps) => (
              <KeyboardArrowDown
                fontSize="small"
                sx={{ fill: theme.palette.grey[400] }}
                {...iconProps}
              />
            )}
            slotProps={{
              root: {
                sx: { height: '30px', backgroundColor: '#fff' },
              },
            }}
            MenuProps={{
              disablePortal: true,
              PaperProps: {
                sx: {
                  width: 'auto',
                  maxHeight: '224px',
                  boxShadow:
                    'rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px',
                },
              },
            }}
            value={currentYear}
            onChange={handleYearChange}
          >
            {generateYears(availableYearRange).map((year) => (
              <MenuItem key={year} value={year}>
                <Typography sx={{ fontSize: '14px' }}>{year}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mr: '10px' }}>
        <IconButton
          disableRipple
          size="small"
          disabled={nextDisabled}
          onClick={onClickNext}
          sx={navButtonSx}
        >
          <KeyboardArrowRight
            fontSize="small"
            sx={{
              fill: nextDisabled ? theme.palette.grey[400] : undefined,
            }}
          />
        </IconButton>
      </Box>
    </Stack>
  );
};
